<template>
  <div style="height: 100%; display: flex; flex-direction: column; overflow: hidden;">
    <portal to="dashboard-title">
      {{ dashboardTitle }}
    </portal>
    <portal to="dashboard-footer">
      <span class="headline font-weight-medium">
        {{ now }}
      </span>
    </portal>

    <DashboardLoading v-if="loading" />

    <v-container
      v-else
      fluid
      class="pa-0"
      v-resize="onResize"
      style="flex-grow: 1; overflow-y: hidden;"
    >
      <div
        style="margin: 0 auto;"
        :style="`height: ${height}px; width: ${width}px;`"
      >
        <v-row style="height: 100%;">
          <v-col cols="6" style="height: 100%; padding: 14px 8px 0px 14px;">
            <v-row no-gutters style="height: 66.6666666%; margin: 0px -4px 0px;">
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <ProductionStatus :scale="scale"></ProductionStatus>
              </v-col>
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <OEEShiftwise widgetitle="Cell OEE Shiftwise: Yesterday" :scale="scale" />
              </v-col>
            </v-row>
            <v-row no-gutters style="height: 33.3333333%; margin: 0px -4px 0px;">
              <v-col cols="12" style="height: 100%; padding: 4px;">
                <OEEMonthwise widgetitle="OEE: 6 Month" :scale="scale" />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6" style="height: 100%; padding: 14px 16px 0px 0px;">
            <v-row no-gutters style="height: 33.3333333%; margin: 0px -4px 0px;">
              <v-col cols="12" style="height: 100%; padding: 4px;">
                <OEELive title="OEE: Live (%)" :scale="scale"></OEELive>
              </v-col>
            </v-row>
            <v-row no-gutters style="height: 33.3333333%; margin: 0px -4px 0px;">
              <v-col cols="12" style="height: 100%; padding: 4px;">
                <OEEDaywise title="Last 7 Days (%)" :scale="scale" :lastdays="7" />
              </v-col>
            </v-row>
            <v-row no-gutters style="height: 33.3333333%; margin: 0px -4px 0px;">
              <v-col cols="12" style="height: 100%; padding: 4px;">
                <OEEDaywise title="Last 15 Days (%)" :scale="scale" :lastdays="15" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <v-app-bar
      flat
      v-if="isTV || isFullscreen"
      :color="$vuetify.theme.dark ? '#121212' : 'grey lighten-5'"
    >
      <img
        :src="`/assets/logo/${logoName}.png`"
        contain
        :alt="altText"
        :height="logoSize"
      />
      <v-toolbar-title
        :class="$vuetify.breakpoint.mdAndUp ? 'headline font-weight-medium ml-4' : 'title pl-0'"
      >
        <portal-target name="dashboard-title"></portal-target>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <portal-target name="dashboard-footer"></portal-target>
    </v-app-bar>
  </div>
</template>

<script>
import {
  mapState,
  mapMutations,
  mapActions,
  mapGetters,
} from 'vuex';

import OEELive from '../components/widgets/OEELive.vue';
import OEEShiftwise from '../components/widgets/OEEShiftwise.vue';
import OEEDaywise from '../components/widgets/OEEDaywise.vue';
import OEEMonthwise from '../components/widgets/OEEMonthwise.vue';
import ProductionStatus from '../components/widgets/ProductionStatus.vue';
import DashboardLoading from '../components/DashboardLoading.vue';

export default {
  name: 'cellwiseProductionDashboard',
  components: {
    ProductionStatus,
    OEELive,
    OEEShiftwise,
    OEEDaywise,
    OEEMonthwise,
    DashboardLoading,
  },
  data() {
    return {
      scale: 0,
      width: 0,
      height: 0,
      ratio: 16 / 9,
      loading: false,
      now: '',
      currentimeinterval: null,
      dashboardBootstrapped: false,
      dashboardUserKey: '',
    };
  },
  computed: {
    ...mapState('cellwiseProductionDashboard', ['serverTime', 'config', 'userId', 'customerId', 'username', 'theme']),
    ...mapGetters('helper', ['primaryLogo', 'isTV']),
    ...mapState('helper', ['isFullscreen']),

    dashboardTitle() {
      const cell = this.$route.query.selectedcell || this.$route.query.cellname;
      if (cell) {
        return `Production Dashboard - ${cell}`;
      }
      return 'Production Dashboard';
    },
    logoName() {
      return this.$vuetify.theme.dark
        ? `${this.primaryLogo.name}-dark`
        : `${this.primaryLogo.name}-light`;
    },
    logoSize() {
      return this.primaryLogo.size;
    },
    altText() {
      return `${this.primaryLogo.name} logo`;
    },
    showFooter() {
      return this.footer?.display;
    },
  },
  watch: {
    serverTime() {
      this.setTime();
    },
    $route: {
      immediate: true,
      async handler(val) {
        const query = val ? val.query : {};
        const nextQuery = { ...query };
        let shouldReplaceQuery = false;
        const routeCell = query.selectedcell || query.cellname;
        const userKey = `${query.userid || ''}|${query.customerid || ''}|${query.username || ''}`;

        let urlTheme = query.theme;
        if (urlTheme !== 'dark' && urlTheme !== 'light') {
          const localDark = localStorage.getItem('dark');
          urlTheme = (localDark === 'false' || localDark === false) ? 'light' : 'dark';
        }

        if (query.theme !== urlTheme) {
          nextQuery.theme = urlTheme;
          shouldReplaceQuery = true;
        }

        if (shouldReplaceQuery) {
          this.$router.replace({ query: nextQuery }).catch(() => {});
          return;
        }

        const isDark = urlTheme === 'dark';
        this.$vuetify.theme.dark = isDark;
        if (this.theme && this.theme.isDark !== isDark) {
          this.setTheme({ ...this.theme, isDark });
        }

        if (!this.dashboardBootstrapped || this.dashboardUserKey !== userKey) {
          this.dashboardUserKey = userKey;
          await this.initializeDashboard(query);
        } else if (routeCell) {
          this.setSelectedCell(routeCell);
        }
      },
    },
  },
  mounted() {
    if (this.theme && this.theme.themes) {
      const activeTheme = this.theme.isDark ? 'dark' : 'light';
      this.$vuetify.theme.dark = this.theme.isDark;

      const customColors = this.theme.themes[activeTheme];
      Object.entries(customColors).forEach(([key, value]) => {
        this.$vuetify.theme.themes[activeTheme][key] = value;
      });
    }
    this.onResize();
    this.setTime();
    this.currentimeinterval = setInterval(async () => {
      await this.getBusinessTime();
      this.setTime();
    }, 120000);
  },
  destroyed() {
    clearInterval(this.currentimeinterval);
  },
  methods: {
    ...mapMutations('cellwiseProductionDashboard', [
      'setUserId',
      'setCustomerId',
      'setUsername',
      'setDataFromJSON',
      'setConfigJson',
      'setIntervalFetch',
      'setSelectedCell',
      'setTheme',
    ]),
    ...mapActions('cellwiseProductionDashboard', ['getServerTime', 'getFileData', 'getMachines', 'getBusinessTime', 'allShifts']),

    async initializeDashboard(query) {
      this.loading = true;

      const { userid, customerid, username } = query;
      let finalUserId = +userid;
      let finalCustomerId = +customerid;
      let finalUsername = username;

      if (!finalUserId || !finalCustomerId) {
        try {
          const swxUser = JSON.parse(localStorage.getItem('user'));
          if (swxUser) {
            finalUserId = swxUser.id;
            finalCustomerId = swxUser.customer?.id || swxUser.customer_id;
            finalUsername = swxUser.email || swxUser.username;
          }
        } catch (e) {
          console.warn('Could not parse global user state from localStorage');
        }
      }

      this.setUserId(finalUserId);
      this.setCustomerId(finalCustomerId);
      this.setUsername(finalUsername || 'admin@shopworx');
      await this.fetchConfig();
      await this.allShifts();
      await this.getBusinessTime();
      await this.getMachines();

      const machines = this.$store.state.cellwiseProductionDashboard.machines || [];
      const allCells = machines.map((m) => m.machinecell).filter((c) => c && c.trim() !== '');
      const uniqueCells = [...new Set(allCells)].sort();

      if (uniqueCells.length > 0) {
        const STORAGE_KEY = 'cellwiseProduction_selectedCell';
        const storedCell = localStorage.getItem(STORAGE_KEY);
        let targetCell = query.selectedcell || query.cellname;

        if (!targetCell || !uniqueCells.includes(targetCell)) {
          if (storedCell && uniqueCells.includes(storedCell)) {
            targetCell = storedCell;
          } else {
            const [firstCell] = uniqueCells;
            targetCell = firstCell;
          }
        }

        localStorage.setItem(STORAGE_KEY, targetCell);
        this.setSelectedCell(targetCell);

        if (this.$route.query.selectedcell !== targetCell) {
          this.$router.replace({
            query: { ...this.$route.query, selectedcell: targetCell },
          }).catch(() => {});
        }
      }

      this.dashboardBootstrapped = true;
      this.loading = false;
    },
    async fetchConfig() {
      const configData = await this.getFileData('config.json');
      if (configData) {
        this.setConfigJson(configData);
        if (configData.jsondata) this.setDataFromJSON(true);
        if (configData.liveOEEandStatusIntervalinSec) {
          this.setIntervalFetch(configData.liveOEEandStatusIntervalinSec);
        }
      }
    },

    onResize() {
      let winHeight = window.innerHeight;
      const winWidth = window.innerWidth;

      if (this.isTV || this.isFullscreen) {
        winHeight -= 64;
      } else {
        winHeight -= 112;
      }

      if (winWidth / this.ratio < winHeight - 40) {
        const height = winWidth / this.ratio;
        const width = height * this.ratio;
        this.width = width - 6;
        this.height = height;
      } else {
        const width = winHeight * this.ratio;
        const height = width / this.ratio;
        this.width = width - 6;
        this.height = height + 10;
      }

      this.$nextTick(() => {
        this.scale = this.width / 1100;
      });
    },
    setTime() {
      this.now = this.dateFormat();
    },
    dateFormat() {
      if (!this.serverTime) return '';
      const date = new Date(this.serverTime);
      const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
      const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
      const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
      const currentHours = date.getHours();
      const currentMin = date.getMinutes();
      const hour = (`0${currentHours}`).slice(-2);
      const min = (`0${currentMin}`).slice(-2);
      return `${day} ${month} ${year} | ${hour}:${min}`;
    },
  },
};
</script>
